import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Radio as RadioIcon, MapPin, Plus, X, Check, Loader2, Minus, Info } from "lucide-react";
import { sr } from "../../lib/sr";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const money = (n, symbol = "$") => `${symbol}${fmt(n)}`;

// ---------------------------------------------------------------------------
// Geography picker — target geographies the client is advertising in.
// Seeded from the Google business location; supports national (many metros)
// and online/delivery businesses that reach beyond their storefront.
// ---------------------------------------------------------------------------
export function GeographyPicker({ onChange, compact }) {
  const [geos, setGeos] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = (data) => {
    setGeos(data.geographies || []);
    setMarkets(data.radio_markets || []);
    onChange && onChange(data);
  };

  useEffect(() => {
    sr.geographies().then(apply).catch(() => setGeos([]));
  }, []); // eslint-disable-line

  const save = async (next) => {
    setSaving(true);
    try {
      const data = await sr.setGeographies(next);
      apply(data);
    } catch { toast.error("Could not save geographies"); }
    finally { setSaving(false); }
  };

  const add = () => {
    const v = input.trim();
    if (!v) return;
    if ((geos || []).some((g) => g.toLowerCase() === v.toLowerCase())) { setInput(""); return; }
    setInput("");
    save([...(geos || []), v]);
  };
  const remove = (g) => save((geos || []).filter((x) => x !== g));

  const marketFor = (g) => {
    const dfw = markets.find((m) => m.key === "dfw" && (m.matched_geographies || []).includes(g));
    const dubai = markets.find((m) => m.key === "dubai" && (m.matched_geographies || []).includes(g));
    return dfw ? "dfw" : dubai ? "dubai" : null;
  };

  if (geos === null) return <div style={{ padding: 12 }}><Loader2 className="sr-spin" size={16} /></div>;

  return (
    <div data-testid="geo-picker">
      <strong style={{ color: "var(--navy)", display: "inline-flex", gap: 6, alignItems: "center", fontSize: compact ? 14 : 15 }}>
        <MapPin size={16} /> Where are you advertising?
      </strong>
      <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 10 }}>
        Seeded from your business location. Add every city or region you're targeting — national brands and online/delivery businesses can add markets beyond their storefront.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }} data-testid="geo-chips">
        {(geos || []).length === 0 && <span className="sr-muted" style={{ fontSize: 13 }}>No geographies yet — add your first market below.</span>}
        {(geos || []).map((g) => {
          const mk = marketFor(g);
          return (
            <span key={g} data-testid={`geo-chip-${g}`} style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 999,
              background: mk ? "#FDF6E7" : "var(--surface)", border: `1px solid ${mk ? "var(--gold)" : "var(--border)"}`,
              fontSize: 13, color: "var(--navy)",
            }}>
              <MapPin size={12} style={{ color: mk ? "var(--gold)" : "var(--muted)" }} />
              {g}
              {mk === "dfw" && <span className="sr-badge sr-badge-gold" style={{ fontSize: 10 }}>FunAsia radio</span>}
              {mk === "dubai" && <span className="sr-badge" style={{ fontSize: 10, background: "#EAF2FF", color: "var(--blue)" }}>Dubai radio</span>}
              <button onClick={() => remove(g)} data-testid={`geo-remove-${g}`} title="Remove"
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 0, display: "inline-flex" }}>
                <X size={13} />
              </button>
            </span>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input className="sr-input" placeholder="Add a city or region (e.g. Dubai, Dallas, Chicago)" value={input}
          onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          data-testid="geo-input" style={{ flex: 1 }} />
        <button className="sr-btn sr-btn-sm" onClick={add} disabled={saving} data-testid="geo-add">
          {saving ? <Loader2 size={14} className="sr-spin" /> : <><Plus size={14} /> Add</>}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One market's station selector (all stations on by default; editable spots).
// ---------------------------------------------------------------------------
function MarketCard({ market, planId }) {
  const dayparts = market.dayparts || [];
  const allDp = dayparts.map((d) => d.key);
  const [sel, setSel] = useState(() =>
    Object.fromEntries(market.stations.map((s) => [s.key, { on: true, spots: s.default_spots, dayparts: s.default_dayparts || allDp }])));
  const [quote, setQuote] = useState(null);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const timer = useRef(null);
  const symbol = market.symbol || "$";

  const selectionPayload = useMemo(
    () => market.stations.filter((s) => sel[s.key]?.on && Number(sel[s.key]?.spots) > 0)
      .map((s) => ({ key: s.key, spots: Number(sel[s.key].spots), dayparts: sel[s.key].dayparts })), [sel, market.stations]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!selectionPayload.length) { setQuote({ total: 0, total_spots: 0, lines: [], audience: market.key === "dfw" ? { modeled_impressions: 0 } : null }); return; }
    timer.current = setTimeout(() => {
      sr.radioQuote({ market: market.key, stations: selectionPayload, bundle: true }).then(setQuote).catch(() => {});
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [selectionPayload, market.key]); // eslint-disable-line

  const toggle = (k) => { setBooked(false); setSel((s) => ({ ...s, [k]: { ...s[k], on: !s[k].on } })); };
  const bump = (k, d) => { setBooked(false); setSel((s) => ({ ...s, [k]: { ...s[k], spots: Math.max(0, Number(s[k].spots || 0) + d) } })); };
  const setSpots = (k, v) => { setBooked(false); setSel((s) => ({ ...s, [k]: { ...s[k], spots: Math.max(0, Math.floor(Number(v) || 0)) } })); };
  const toggleDaypart = (k, dp) => {
    setBooked(false);
    setSel((s) => {
      const cur = s[k].dayparts || allDp;
      const next = cur.includes(dp) ? cur.filter((x) => x !== dp) : [...cur, dp];
      return { ...s, [k]: { ...s[k], dayparts: next.length ? next : allDp } };
    });
  };

  const book = async () => {
    if (!selectionPayload.length) { toast.error("Select at least one station with spots"); return; }
    setBooking(true);
    try {
      await sr.bookRadioSelection({ market: market.key, stations: selectionPayload, plan_id: planId });
      setBooked(true);
      toast.success(`${market.network} sent to Ops — they'll confirm rates with the stations`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send booking"); }
    finally { setBooking(false); }
  };

  return (
    <div className="sr-card" style={{ marginTop: 12, background: "#fff" }} data-testid={`radio-market-${market.key}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div>
          <strong style={{ color: "var(--navy)", display: "inline-flex", gap: 6, alignItems: "center" }}>
            <RadioIcon size={16} /> {market.network}
          </strong>
          <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 2 }}>{market.label} · {market.blurb}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "var(--navy)" }} data-testid={`radio-total-${market.key}`}>
            {money(quote?.total, symbol)}<span className="sr-muted" style={{ fontSize: 12, fontWeight: 600 }}>/mo</span>
          </div>
          <div className="sr-muted" style={{ fontSize: 11.5 }}>{quote?.total_spots || 0} spots · {market.currency}</div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
        {market.stations.map((s) => {
          const on = sel[s.key]?.on;
          return (
            <div key={s.key} data-testid={`radio-station-${s.key}`} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10,
              border: `1px solid ${on ? "var(--border)" : "var(--border)"}`, background: on ? "var(--surface)" : "#fff", opacity: on ? 1 : 0.55,
            }}>
              <img src={s.logo} alt={s.name} width={38} height={38} loading="lazy"
                style={{ borderRadius: 8, objectFit: "contain", background: "#fff", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }}>{s.name}</div>
                <div className="sr-muted" style={{ fontSize: 12 }}>
                  {s.delivery} · {money(s.unit_price, symbol)}/spot
                  {market.key === "dfw" && (s.has_audience ? "" : " · audience TBD")}
                </div>
                {on && dayparts.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }} data-testid={`radio-dayparts-${s.key}`}>
                    {dayparts.map((d) => {
                      const active = (sel[s.key]?.dayparts || allDp).includes(d.key);
                      return (
                        <button key={d.key} onClick={() => toggleDaypart(s.key, d.key)} data-testid={`radio-daypart-${s.key}-${d.key}`}
                          title={`${d.label} (${d.time})`} style={{
                            fontSize: 11, padding: "3px 8px", borderRadius: 999, cursor: "pointer",
                            border: `1px solid ${active ? "var(--gold)" : "var(--border)"}`,
                            background: active ? "#FDF6E7" : "#fff", color: active ? "var(--navy)" : "var(--muted)",
                            fontWeight: active ? 700 : 500,
                          }}>
                          {d.drive_time ? "🚗 " : ""}{d.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {on ? (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => bump(s.key, -1)} data-testid={`radio-spots-minus-${s.key}`} style={{ padding: 4 }}><Minus size={14} /></button>
                  <input className="sr-input" type="number" min="0" value={sel[s.key]?.spots} onChange={(e) => setSpots(s.key, e.target.value)}
                    data-testid={`radio-spots-${s.key}`} style={{ width: 62, textAlign: "center", padding: "5px 6px" }} />
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => bump(s.key, 1)} data-testid={`radio-spots-plus-${s.key}`} style={{ padding: 4 }}><Plus size={14} /></button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => toggle(s.key)} data-testid={`radio-remove-${s.key}`} title="Remove station" style={{ padding: 4, color: "var(--muted)" }}><X size={14} /></button>
                </div>
              ) : (
                <button className="sr-btn sr-btn-sm" onClick={() => toggle(s.key)} data-testid={`radio-add-${s.key}`}><Plus size={13} /> Add</button>
              )}
            </div>
          );
        })}
      </div>

      {market.key === "dfw" && quote?.audience && (
        <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 10 }} data-testid={`radio-audience-${market.key}`}>
          Estimated <strong style={{ color: "var(--navy)" }}>{fmt(quote.audience.modeled_impressions)}</strong> modeled monthly impressions
          {quote.audience.needs_audience?.length ? ` · audience for ${quote.audience.needs_audience.join(", ")} not modeled yet` : ""}.
          <span style={{ fontStyle: "italic" }}> Planning estimate, not guaranteed exposure.</span>
        </div>
      )}
      {market.key === "dubai" && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10, background: "#EAF2FF", border: "1px solid #C7D7FE", borderRadius: 8, padding: "8px 11px" }}>
          <Info size={14} style={{ color: "var(--blue)", flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12.5, color: "var(--navy)" }}>Dubai rates are quoted in dirhams (AED). Ops confirms availability and final pricing with the UAE stations before airing.</div>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        {booked ? (
          <span className="sr-badge sr-badge-green" data-testid={`radio-booked-${market.key}`}><Check size={12} /> Sent to Ops — they'll confirm rates before airing</span>
        ) : (
          <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={book} disabled={booking || !selectionPayload.length} data-testid={`radio-book-${market.key}`}>
            {booking ? <Loader2 size={13} className="sr-spin" /> : <><RadioIcon size={13} /> {market.key === "dubai" ? "Request booking (AED quote)" : "Approve & book"}</>}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Full radio module: geography picker + geo-gated market station selectors.
// Radio only appears for DFW (FunAsia) and Dubai/UAE; hidden everywhere else.
// ---------------------------------------------------------------------------
export function RadioPlanner({ planId }) {
  const [markets, setMarkets] = useState(null);
  const [hasGeos, setHasGeos] = useState(false);

  const refresh = () => sr.radioMarkets().then((d) => {
    setMarkets(d.markets || []);
    setHasGeos((d.geographies || []).length > 0);
  }).catch(() => setMarkets([]));

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  return (
    <div className="sr-card" style={{ marginBottom: 16 }} data-testid="radio-planner">
      <GeographyPicker onChange={(d) => { setHasGeos((d.geographies || []).length > 0); refresh(); }} />

      {markets === null ? (
        <div style={{ marginTop: 12 }}><Loader2 className="sr-spin" size={16} /></div>
      ) : markets.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <strong style={{ color: "var(--navy)", display: "inline-flex", gap: 6, alignItems: "center" }}><RadioIcon size={16} /> Radio — pick your stations</strong>
            <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 4 }}>All stations are selected by default. Adjust spots or remove any station you don't want. Radio is a separate booking from your digital budget.</p>
          </div>
          {markets.map((m) => <MarketCard key={m.key} market={m} planId={planId} />)}
        </div>
      ) : hasGeos ? (
        <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12, display: "flex", gap: 8, alignItems: "flex-start" }} data-testid="radio-unavailable">
          <Info size={15} style={{ color: "var(--muted)", flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: "var(--navy)" }}>
            Radio isn't available for your selected geographies — but every other channel is. We currently offer radio in <strong>Dallas–Fort Worth</strong> (FunAsia) and <strong>Dubai / UAE</strong>. Add one of those above to unlock it.
          </div>
        </div>
      ) : null}
    </div>
  );
}
